import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";
import { Box, Container, Divider, Grid, Typography } from "@mui/material";
import { designTokens } from "../../../theme/designTokens";
import {
  aboutHeroContent,
  aboutHeroFeatures,
  aboutHeroStats,
} from "../../../theme/aboutContent";

const { colors, fonts, spacing } = designTokens;

const featureIcons = [
  <VerifiedUserOutlinedIcon sx={{ fontSize: 32, color: colors.primary }} />,
  <GroupsOutlinedIcon sx={{ fontSize: 32, color: colors.primary }} />,
  <TrendingUpOutlinedIcon sx={{ fontSize: 32, color: colors.primary }} />,
];

const statIcons = [
  <WorkOutlineIcon sx={{ fontSize: 42, color: colors.primary }} />,
  <BusinessCenterOutlinedIcon sx={{ fontSize: 42, color: colors.primary }} />,
  <ThumbUpAltOutlinedIcon sx={{ fontSize: 42, color: colors.primary }} />,
  <PsychologyOutlinedIcon sx={{ fontSize: 42, color: colors.primary }} />,
];

const AboutHeroSection = () => (
  <Box
    sx={{
      pt: { xs: `${spacing.headerHeight.xs + 24}px`, md: `${spacing.headerHeight.md + 32}px` },
      pb: 0,
    }}
  >
    <Container maxWidth={spacing.containerMax}>
      <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography
            component="h1"
            sx={{
              fontWeight: fonts.weights.bold,
              fontSize: { xs: "2rem", sm: "2.25rem", md: "3rem" },
              lineHeight: { xs: 1.25, md: "55px" },
              letterSpacing: { md: "2px" },
              color: colors.textPrimary,
              mb: 2,
            }}
          >
            {aboutHeroContent.title}{" "}
            <Box component="span" sx={{ color: colors.primary }}>
              {aboutHeroContent.titleHighlight}
            </Box>
          </Typography>

          <Divider
            sx={{
              backgroundColor: colors.primary,
              width: { xs: 200, md: 312 },
              height: 5,
              mb: 3,
              borderRadius: "4px",
            }}
          />

          <Typography
            sx={{
              fontSize: { xs: "1rem", md: "1.25rem" },
              lineHeight: "30px",
              color: colors.textPrimary,
              maxWidth: 533,
            }}
          >
            <Box component="span" sx={{ fontWeight: fonts.weights.bold }}>
              Sundeus Technology
            </Box>{" "}
            is a team of technologists and innovators dedicated to helping businesses grow through
            smart digital solutions. Operating across the{" "}
            <Box component="span" sx={{ fontWeight: fonts.weights.bold }}>
              UK, US,
            </Box>{" "}
            and{" "}
            <Box component="span" sx={{ fontWeight: fonts.weights.bold }}>
              India
            </Box>
            , we combine expertise in cloud, AI, and enterprise technology to deliver impactful,
            scalable results.
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{
              border: `2px solid ${colors.primary}`,
              borderRadius: "20px",
              overflow: "hidden",
              boxShadow:
                "0px 4px 8px 3px rgba(0,0,0,0.15), 0px 1px 3px 0px rgba(0,0,0,0.3)",
              maxWidth: 668,
              ml: { md: "auto" },
            }}
          >
            <Box
              component="img"
              src={aboutHeroContent.image}
              alt={aboutHeroContent.imageAlt}
              sx={{
                width: "100%",
                height: { xs: 260, sm: 320, md: 387 },
                objectFit: "cover",
                display: "block",
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Container>

    <Box
      sx={{
        mt: { xs: 4, md: 5 },
        borderTop: `1px solid ${colors.border}`,
        pt: { xs: 3, md: 4 },
        pb: { xs: 3, md: 4 },
      }}
    >
      <Container maxWidth={spacing.containerMax}>
        <Grid container spacing={{ xs: 3, md: 2 }} alignItems="flex-start">
          <Grid item xs={12} lg={6}>
            <Grid container spacing={{ xs: 3, sm: 2 }}>
              {aboutHeroFeatures.map((feature, index) => (
                <Grid item xs={12} sm={4} key={feature.title}>
                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                    <Box sx={{ flexShrink: 0, mt: 0.5 }}>{featureIcons[index]}</Box>
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: fonts.weights.bold,
                          fontSize: "0.9375rem",
                          // lineHeight: "30px",
                          color: colors.textPrimary,
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.9375rem",
                          // lineHeight: "30px",
                          color: colors.textPrimary,
                        }}
                      >
                        {feature.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Grid container spacing={2} justifyContent={{ xs: "center", lg: "flex-end" }}>
              {aboutHeroStats.map((stat, index) => (
                <Grid item xs={6} sm={3} key={stat.label}>
                  <Box
                    sx={{
                      backgroundColor: "rgba(217,217,217,0.1)",
                      borderRadius: "10px",
                      boxShadow: `0px 4px 4px 0px ${colors.primary}`,
                      height: { xs: 150, md: 182 },
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 1,
                      textAlign: "center",
                    }}
                  >
                    <Box sx={{ mb: 0.5 }}>{statIcons[index]}</Box>
                    <Typography
                      sx={{
                        fontWeight: fonts.weights.bold,
                        fontSize: { xs: "1.75rem", md: "2.5rem" },
                        lineHeight: "30px",
                        color: colors.textPrimary,
                      }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        lineHeight: "30px",
                        color: colors.textPrimary,
                        mt: 0.5,
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  </Box>
);

export default AboutHeroSection;
